import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

const SUPPORTED_LOCALES = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
];

export interface LanguageSwitcherProps {
  /**
   * "chrome" is the app-shell look used by Header (light, Tailwind tokens).
   * "dark" matches the Home5 / SubscriptionPlans / LoginForm slate + violet
   * system — those pages style themselves rather than inheriting the dashboard
   * shell, so the switcher has to carry their palette to sit on them.
   */
  variant?: 'chrome' | 'dark';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'chrome',
  className = '',
}) => {
  const { i18n } = useTranslation();
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSwitch = (newLocale: string) => {
    if (newLocale === locale) return;
    // Replace the leading locale segment only. A plain replace(`/${locale}`)
    // also matched the start of a longer segment — `/en/entries` came out as
    // `/tries` — so the match is anchored and must end at a `/` or the string.
    const pathWithoutLocale = locale
      ? location.pathname.replace(new RegExp(`^/${locale}(?=/|$)`), '')
      : location.pathname;
    const newPath = `/${newLocale}${pathWithoutLocale || ''}`;
    i18n.changeLanguage(newLocale);
    localStorage.setItem('i18nextLng', newLocale);
    navigate(newPath + location.search + location.hash);
  };

  if (variant === 'dark') {
    return (
      <div className={`lang-dark ${className}`.trim()} role="group" aria-label="Language">
        <style>{LANG_DARK_CSS}</style>
        {SUPPORTED_LOCALES.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            onClick={() => handleSwitch(code)}
            aria-pressed={locale === code}
            className={`lang-dark-btn${locale === code ? ' is-active' : ''}`}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex items-center rounded-md border border-gray-200 overflow-hidden ${className}`.trim()}>
      {SUPPORTED_LOCALES.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => handleSwitch(code)}
          aria-pressed={locale === code}
          className={`px-2.5 py-1 text-xs font-semibold transition-colors duration-150 ${
            locale === code
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

// Tokens are the dark pages' own: the .sp-badge / .h5-btn translucent surface
// for the track, the violet primary-button gradient for the selected locale.
const LANG_DARK_CSS = `
.lang-dark{display:inline-flex;align-items:center;padding:3px;gap:2px;border-radius:10px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.14)}
.lang-dark-btn{font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;letter-spacing:.04em;line-height:1;padding:6px 10px;border:none;border-radius:7px;background:transparent;color:#94a3b8;cursor:pointer;transition:color .25s,background .25s,box-shadow .25s}
.lang-dark-btn:hover:not(.is-active){color:#c4b5fd;background:rgba(255,255,255,.06)}
.lang-dark-btn:focus-visible{outline:2px solid rgba(167,139,250,.7);outline-offset:2px}
.lang-dark-btn.is-active{background:linear-gradient(135deg,#a78bfa,#8b5cf6);color:#fff;box-shadow:0 4px 14px rgba(139,92,246,.34)}
@media(prefers-reduced-motion:reduce){.lang-dark-btn{transition-duration:.001ms}}
`;
